import { useMemo, useState, type ReactNode } from 'react';
import { ShieldCheckIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  SCREEN_BAR_HEIGHT,
  ScreenBar,
  ScreenBarCenter,
  ScreenBarEnd,
  ScreenBarRow,
  ScreenBarStart,
} from '@voll-intelligence/ui-kit/ui/screen-bar';
import {
  DEFAULT_RENTAL_RESULTS_ORDER,
  EMPTY_RENTAL_RESULTS_FILTERS,
  matchesRentalResultsFilters,
  rentalOfferPagesNotice,
  rentalPageCurrency,
  RentalResultsPage,
  sortRentalOffers,
  rentalFilterCatalogOf,
  rentalServerFiltersOf,
  toRentalFilterableOffers,
  useRentalOfferPages,
  type RentalResultsFiltersCopy,
  type RentalResultsFilterValue,
  type RentalResultsOrder,
  type RentalResultsPageCopy,
  type RentalResultsSource,
  RentalSummaryPill,
  type RentalSummary,
} from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { fmtMoney } from '../../../shared/ui/flight_format.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { ScreenDialog } from '@voll-intelligence/ui-kit/ui/screen-dialog';
import { rentalResultsViewCopy, rentalStopsOf } from './rental_offer_views.js';
import { rentalVehicleCopy } from '../../shared/rental_vehicle_copy.js';
import { ScreenBarClose, useScreenBarTrail } from '../../../shared/ui/screen_bar_parts.js';
import type {
  ChatUiRentalOffer,
  ChatUiRentalSearchMeta,
  ChatUiRentalTravelerPolicy,
  RentalOfferPageHint,
} from '@voll-intelligence/genui-contract';
import { RentalPolicyDrawer } from './components/policy_drawer.js';

/*
 * A TELA EM DIÁLOGO "Ver todos os carros" da conversa — a MESMA página determinística de
 * resultados da bancada de carro (`RentalResultsPage`, #2441 sobre o
 * `ResultsLayout` #2994: título, meta + ordenador, coluna de cortes, cards),
 * por cima da conversa inteira, na anatomia da casca do explorer de voos e do
 * takeover do hotel: a barra do topo é o ✕ que sai, a pill de contexto da busca
 * (lugar · janela da locação) e, no canto direito, o `trailing` do host (a mala
 * da viagem, #3301). Substituiu o drawer lateral próprio (`RentalSearchDrawer`,
 * apagado): eram duas geometrias para a mesma lista, com filtros, contagem e
 * cards que não batiam com a bancada em nenhum slot.
 *
 * Os DADOS seguem os do drawer: a página 1 vem do refino determinístico do
 * shell (`GenUiConfig.rentalRefine` → BFF `POST /rental/search`) com os
 * filtros que o servidor corta (`rentalServerFiltersOf`), e as seguintes só
 * acrescentam; o resto do corte é client-side sobre tudo o que já veio, como na
 * bancada (as contagens dos filtros são da busca inteira). A lista
 * nasce com o preview do card, para a página não abrir vazia enquanto a 1ª
 * página chega.
 *
 * Sem mapa: a busca conversacional não traz coordenada nenhuma (a oferta só
 * nomeia a agência), e um painel "mapa indisponível" ocuparia a coluna à toa —
 * a página centra a linha, como a do voo. A pill é somente-leitura (sem lupa
 * nem expansão): não há formulário para voltar nem seam de re-busca na
 * conversa — o pedido é do agente. "Ver políticas" mora ao lado do título, no
 * slot que na bancada é o compartilhar.
 */

/** A escolha na página: a oferta, a posição na tela e a página do BFF que a
 *  trouxe, com os filtros que o servidor cortou — o agente relê essa página
 *  para achar a oferta que ele não buscou (#5833). */
type SelectOffer = (offer: ChatUiRentalOffer, position: number, page: RentalOfferPageHint) => void;

/** Uma oferta do refino com os filtros da consulta que a trouxe: com filtro de
 *  servidor novo, as linhas velhas ficam em cena até a página 1 chegar, e o
 *  "Alugar" de uma delas tem de levar a página E os filtros dela, não os novos.
 *  Sem `pageFilters`, é o preview do card: a página 1 da busca, sem filtro. */
type RefinedOffer = ChatUiRentalOffer & { pageFilters?: RentalOfferPageHint['filters'] };

// ── Rótulos ──────────────────────────────────────────────────────────────────

/** O resumo da pill: os pontos de `stopsOf` e a janela da primeira oferta
 *  (o bloco não ecoa o pedido). */
function rentalSummaryOf(
  pickup: RentalResultsSource['pickup'] | undefined,
  dropoff: RentalResultsSource['pickup'] | undefined,
  offer: ChatUiRentalOffer | undefined,
): RentalSummary {
  return {
    pickup: pickup?.label ?? '',
    ...(dropoff ? { dropoff: dropoff.label } : {}),
    ...(offer?.pickupAt ? { pickupAt: offer.pickupAt } : {}),
    ...(offer?.dropoffAt ? { dropoffAt: offer.dropoffAt } : {}),
  };
}

// ── Copy no vocabulário do kit ───────────────────────────────────────────────

type T = ReturnType<typeof useGenUiI18n>['t'];

function pageCopy(t: T, locale: string, place: string, currency: string): RentalResultsPageCopy {
  const price = (amount: number) => fmtMoney({ amount, currency });
  return {
    title: place ? t('rental.results.title', { place }) : t('rental.drawer.title'),
    totalCarsLabel: (total) =>
      t(total === 1 ? 'rental.results.total_one' : 'rental.results.total_other', {
        count: total,
      }),
    visibleCarsLabel: (visible, total) => t('rental.results.visible', { visible, total }),
    resultsLabel: t('rental.results.resultsLabel'),
    shareLabel: '',
    applyFiltersLabel: t('rental.results.applyFilters'),
    showMapLabel: t('rental.results.showMap'),
    loadMoreLabel: t('rental.results.loadMore'),
    filteredEmpty: {
      title: t('rental.results.filteredEmpty'),
      clearLabel: t('rental.results.clearFilters'),
    },
    sort: {
      sortLabel: t('rental.results.sort'),
      orderLabel: (order) => t(`rental.results.order.${order}`),
    },
    chips: {
      toolbarLabel: t('rental.results.chips.toolbar'),
      backLabel: t('rental.results.chips.back'),
      policyLabel: t('rental.results.chips.policy'),
      dailyPriceLabel: t('rental.results.chips.dailyPrice'),
      priceUpTo: (maximum) => t('rental.results.chips.priceUpTo', { price: price(maximum) }),
      priceFrom: (minimum) => t('rental.results.chips.priceFrom', { price: price(minimum) }),
      priceBetween: (minimum, maximum) =>
        t('rental.results.chips.priceBetween', { min: price(minimum), max: price(maximum) }),
      optionList: (labels) => new Intl.ListFormat(locale, { type: 'disjunction' }).format(labels),
    },
    card: { selectLabel: t('rental.rent'), ...rentalVehicleCopy(t) },
  };
}

function filtersCopy(t: T, currency: string): RentalResultsFiltersCopy {
  return {
    filtersLabel: t('rental.filters'),
    collapseFiltersLabel: t('rental.results.filters.collapse'),
    closeFiltersLabel: t('rental.results.filters.close'),
    resetFiltersLabel: t('rental.reset'),
    policyLabel: t('rental.policy'),
    withinPolicyLabel: t('rental.onlyInPolicy'),
    categoryLabel: t('rental.category'),
    fromPriceLabel: (amount) => fmtMoney({ amount, currency }),
    accessoriesLabel: t('rental.results.filters.accessories'),
    accessoryLabel: (accessory) =>
      accessory === 'automatic'
        ? t('rental.results.filters.automatic')
        : t('rental.results.filters.airConditioning'),
    dailyPriceLabel: t('rental.results.filters.dailyPrice'),
    minimumPriceLabel: t('rental.results.filters.minimumPrice'),
    maximumPriceLabel: t('rental.results.filters.maximumPrice'),
    companyLabel: t('rental.results.filters.company'),
  };
}

// ── A casca ──────────────────────────────────────────────────────────────────

/** A barra do topo: ✕ · pill de contexto (somente-leitura) · trailing do host,
 *  e a fila de chips do palco estreito na segunda linha. */
function RentalResultsBar({
  summary,
  onChipsSlot,
  onClose,
}: Readonly<{
  summary: RentalSummary;
  onChipsSlot: (element: HTMLElement | null) => void;
  onClose: () => void;
}>): ReactNode {
  const { t, locale } = useGenUiI18n();
  const { takeoverTrailing } = useGenUiConfig();
  const trail = useScreenBarTrail('rental', onClose);
  // O pedido é do agente: a pill é só leitura (sem condutor nem lupa), e no
  // celular a cápsula não vira botão.
  return (
    <ScreenBar>
      <ScreenBarStart>
        <ScreenBarClose label={t('rental.results.close')} onClick={onClose} />
        {trail}
      </ScreenBarStart>
      <ScreenBarCenter>
        <RentalSummaryPill
          summary={summary}
          copy={{ aria: t('rental.results.pillAria') }}
          locale={locale}
          readOnly
        />
      </ScreenBarCenter>
      <ScreenBarEnd>{takeoverTrailing}</ScreenBarEnd>
      <ScreenBarRow ref={onChipsSlot} />
    </ScreenBar>
  );
}

export function RentalResultsDialog({
  offers,
  metadata,
  searchContext,
  policies,
  onReserve,
  onPickVehicle,
  onClose,
}: Readonly<{
  /** O preview do card: a lista em cena até a 1ª página do refino chegar e a
   *  fonte dos pontos da busca (agência de retirada/devolução) e da janela. */
  offers: readonly ChatUiRentalOffer[];
  metadata: ChatUiRentalSearchMeta;
  searchContext: string;
  policies?: ChatUiRentalTravelerPolicy;
  onReserve?: SelectOffer;
  onPickVehicle?: SelectOffer;
  onClose: () => void;
}>): ReactNode {
  const { t, locale } = useGenUiI18n();
  const [filters, setFilters] = useState<RentalResultsFilterValue>(EMPTY_RENTAL_RESULTS_FILTERS);
  const [order, setOrder] = useState<RentalResultsOrder>(DEFAULT_RENTAL_RESULTS_ORDER);
  const [policyOpen, setPolicyOpen] = useState(false);
  // O slot da fila de chips do palco ESTREITO: a segunda linha desta barra,
  // como no takeover do hotel. Estado (e não ref) porque a página porta nele.
  const [chipsSlot, setChipsSlot] = useState<HTMLElement | null>(null);
  // A lista pelo `rentalRefine` do shell (a busca do BFF, com os filtros que o
  // servidor corta): nasce com o preview do card e troca pela página 1 ao
  // chegar; filtro de servidor novo recomeça nela.
  const { rentalRefine } = useGenUiConfig();
  const serverFilters = useMemo(() => rentalServerFiltersOf(filters), [filters]);
  const refined = useRentalOfferPages<RefinedOffer>({
    ...(rentalRefine
      ? {
          fetchPage: async (page: number) => {
            const res = await rentalRefine({ searchContext, filters: serverFilters, page });
            return {
              ...res,
              offers: res.offers.map((row) => ({ ...row, pageFilters: serverFilters })),
            };
          },
        }
      : {}),
    queryKey: JSON.stringify(serverFilters),
    initial: { offers },
  });

  const [pickup, dropoff] = rentalStopsOf(offers);
  const summary = rentalSummaryOf(pickup, dropoff, offers[0]);
  const currency = rentalPageCurrency(refined.offers);
  const select = onReserve ?? onPickVehicle;
  const source = useMemo<RentalResultsSource>(
    () => ({
      facets: metadata,
      pickup: pickup ?? { label: '' },
      dropoff: dropoff ?? { label: '' },
    }),
    [metadata, pickup, dropoff],
  );
  const filterable = useMemo(
    () =>
      toRentalFilterableOffers(
        refined.offers,
        source,
        rentalResultsViewCopy(t),
        select !== undefined,
        refined.pageOf,
      ),
    [refined.offers, source, t, select, refined.pageOf],
  );
  const catalog = useMemo(() => rentalFilterCatalogOf(metadata), [metadata]);
  // A ordem que a pessoa VÊ (o corte e o ordenador da página, as mesmas funções
  // puras do kit): é dela que sai a "opção N" do eco do "Alugar" — a ordem do
  // fornecedor não bate com a tela depois de um filtro ou do ordenador.
  const visibleIds = useMemo(
    () =>
      sortRentalOffers(
        filterable.filter((facts) => matchesRentalResultsFilters(facts, filters)),
        order,
      ).map(({ offer }) => offer.id),
    [filterable, filters, order],
  );
  const copy = useMemo(
    () => pageCopy(t, locale, pickup?.label ?? '', currency),
    [t, locale, pickup?.label, currency],
  );
  const fCopy = useMemo(() => filtersCopy(t, currency), [t, currency]);
  const title = copy.title;

  const notice = rentalOfferPagesNotice(refined, {
    loadingLabel: t('rental.loadingVehicles'),
    errorLabel: t('rental.loadError'),
    retryLabel: t('common.tryAgain'),
  });

  return (
    <ScreenDialog label={title} onClose={onClose}>
      <RentalResultsBar summary={summary} onChipsSlot={setChipsSlot} onClose={onClose} />
      {/* A caixa trava a altura abaixo da barra e a página a enche: na linha a
          lista rola no próprio `<main>` (`listScroll="own"`, #4587), com a barra
          ao lado dela; no palco estreito o rolo volta a ser desta caixa, e as
          colunas fixas descontam a barra acima. */}
      <div
        className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        style={{ '--hui-results-page-chrome': SCREEN_BAR_HEIGHT } as React.CSSProperties}
      >
        <RentalResultsPage
          listScroll="own"
          offers={filterable}
          currency={currency}
          copy={copy}
          filtersCopy={fCopy}
          filters={filters}
          order={order}
          total={metadata.countItems}
          catalog={catalog}
          hasMore={refined.hasMore}
          loadingMore={refined.loading && !refined.settling}
          {...(notice ? { notice } : {})}
          onQueryChange={(next) => {
            setFilters(next.filters);
            setOrder(next.order);
          }}
          onLoadMore={refined.loadMore}
          chipsContainer={chipsSlot}
          onSelect={(offerId) => {
            const offer = refined.offers.find((row) => row.offerId === offerId);
            if (offer && select) {
              select(offer, visibleIds.indexOf(offerId) + 1, {
                searchContext,
                number: refined.pageOf(offerId),
                filters: offer.pageFilters ?? {},
              });
            }
          }}
          {...(policies
            ? {
                titleActions: (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPolicyOpen(true);
                    }}
                  >
                    <ShieldCheckIcon aria-hidden />
                    {t('rental.viewPolicies')}
                  </Button>
                ),
              }
            : {})}
        />
      </div>
      {policyOpen && policies && (
        <RentalPolicyDrawer
          policies={policies}
          onClose={() => {
            setPolicyOpen(false);
          }}
        />
      )}
    </ScreenDialog>
  );
}
